import { gql, useMutation } from '@apollo/client';
import { Button, Callout, Dialog, IconButton, Table, TextField } from '@radix-ui/themes';
import { CircleCheck, CircleMinus, CircleX, Loader, TriangleAlert, User } from 'lucide-react';
import { nanoid } from 'nanoid';
import { Label } from 'radix-ui';
import type { ChangeEvent } from 'react';
import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { toast } from 'sonner';
import './AddCandidateToJob.css';

const INVITE_CANDIDATES_TO_JOB_MUTATION = gql`
  mutation inviteCandidatesToJob($jobId: String!, $candidates: [InviteCandidate!]) {
    inviteCandidatesToJob(input: { jobId: $jobId, candidates: $candidates }) {
      id
      success
      statusMessage
      duplicate
    }
  }
`;

type InviteCandidateForm = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  duplicate?: boolean;
  status?: 'success' | 'error';
  statusMessage?: string;
};

const validateEmail = (email: string): boolean => {
  // Simple RFC 5322 compliant regex approximation
  return /^\S+@\S+\.\S+$/.test(email);
};

const AddCandidateToJob = (props: { jobId?: string }) => {
  const { t } = useTranslation();
  const [hover, setHover] = useState(false);

  const [uploadedCandidates, setUploadedCandidates] = useState<InviteCandidateForm[]>([]);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [showInfoMore, setShowInfoMore] = useState(false);
  const [importDone, setImportDone] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false); // Synchronous guard against double-clicks

  const [inviteCandidatesToJob, { loading: inviteCandidatesToJobLoading }] = useMutation(
    INVITE_CANDIDATES_TO_JOB_MUTATION,
    {
      onCompleted(data) {
        type Result = { id: string; success: boolean; statusMessage: string; duplicate?: boolean };
        const results: Array<Result> = data?.inviteCandidatesToJob ?? [];
        setUploadedCandidates((prev) =>
          prev.map((c) => {
            const res = results.find((r) => r.id === c.id);
            if (!res) {
              return c;
            }
            return {
              ...c,
              status: res.success ? 'success' : 'error',
              statusMessage: res.statusMessage,
              duplicate: res.duplicate,
            } as InviteCandidateForm;
          }),
        );
      },
    },
  );

  const handleSubmit = () => {
    // Use ref for synchronous guard - useState is async and can miss rapid clicks
    if (inviteCandidatesToJobLoading) return;
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    // Add duplicate flag to each candidate based on email duplicates
    const candidatesWithDuplicateFlag = uploadedCandidates.map((c, idx) => ({
      ...c,
      duplicate: duplicateIndexSet.has(idx),
    }));

    inviteCandidatesToJob({
      variables: {
        jobId: props.jobId,
        candidates: candidatesWithDuplicateFlag,
      },
    })
      .then(() => {
        setImportDone(true);
      })
      .catch((e) => {
        toast.error(t('addCandidateToJob.toast.errorInviting'));
        console.error('Error inviting candidates to job', e);
      })
      .finally(() => {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      });
  };

  const parseCsv = (text: string): InviteCandidateForm[] => {
    return text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const parts = line
          .split(';')
          .map((p) => p.trim())
          .filter((p) => p !== '');

        if (parts.length < 4) {
          return null;
        }

        const [firstName, lastName, phone, email] = parts;

        return {
          id: nanoid(),
          firstName: firstName ? firstName.trim() : '',
          lastName: lastName ? lastName.trim() : '',
          phone: phone ?? '',
          email: email ? email.toLowerCase().trim() : '',
        } as InviteCandidateForm;
      })
      .filter((c): c is InviteCandidateForm => c !== null);
  };

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Read file as ArrayBuffer to try multiple encodings
    const reader = new FileReader();
    reader.onload = (event) => {
      const arrayBuffer = event.target?.result as ArrayBuffer;

      // Try UTF-8 first
      const utf8Text = new TextDecoder('utf-8').decode(arrayBuffer);

      // Check if UTF-8 decoding produced replacement characters (�)
      // which indicates the file is not actually UTF-8
      const hasReplacementChar = utf8Text.includes('\uFFFD');

      let text: string;
      if (hasReplacementChar) {
        // Fall back to ISO-8859-1 (common for Excel/Numbers exports with European characters)
        text = new TextDecoder('iso-8859-1').decode(arrayBuffer);
      } else {
        text = utf8Text;
      }

      const candidates = parseCsv(text);
      setUploadedCandidates(candidates);
      setFileInputKey((k) => k + 1);
    };

    reader.readAsArrayBuffer(file);
  };

  const handleCandidateChange = (index: number, field: keyof InviteCandidateForm, value?: string) => {
    setUploadedCandidates((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value } as InviteCandidateForm;
      return updated;
    });
  };

  const addEmptyCandidate = () => {
    setUploadedCandidates((prev) => [...prev, { id: nanoid(), firstName: '', lastName: '', email: '', phone: '' }]);
  };

  const removeCandidate = (index: number) => {
    setUploadedCandidates((prev) => prev.filter((_, idx) => idx !== index));
  };

  const clearAllCandidates = () => {
    setUploadedCandidates([]);
    setFileInputKey((k) => k + 1);
  };

  // Detect duplicate emails (only rows AFTER the first occurrence are considered duplicates)
  const getDuplicateIndexSet = () => {
    const firstSeen: Record<string, number> = {};
    const duplicateIndices: number[] = [];
    for (let idx = 0; idx < uploadedCandidates.length; idx++) {
      const emailKey = uploadedCandidates[idx].email.trim().toLowerCase();
      if (!emailKey) continue;
      if (firstSeen[emailKey] !== undefined) {
        duplicateIndices.push(idx);
      } else {
        firstSeen[emailKey] = idx;
      }
    }
    return new Set(duplicateIndices);
  };
  const duplicateIndexSet = getDuplicateIndexSet();

  return (
    <Dialog.Root>
      <Dialog.Trigger>
        <span
          className="name-pill inline-flex! items-center justify-center"
          onMouseOver={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          <User size={hover ? 24 : 20} />
        </span>
      </Dialog.Trigger>
      <Dialog.Content size="4" aria-describedby={undefined} className="csv-modal">
        <Dialog.Title>{t('addCandidateToJob.dialog.title')}</Dialog.Title>

        <Callout.Root className="mb-4">
          <Callout.Text>
            {t('addCandidateToJob.info.intro')}
            <br />
            <code>firstName; lastName; phoneNumber; email;</code>
            {!showInfoMore && (
              <>
                <br />
                <Button variant="ghost" className="m-0" onClick={() => setShowInfoMore(true)}>
                  {t('addCandidateToJob.info.readMoreButton')}
                </Button>
              </>
            )}
            {showInfoMore && (
              <>
                <br />• {t('addCandidateToJob.info.nameNote')}
                <br />• {t('addCandidateToJob.info.invalidEmailHighlight')}
                <br />• {t('addCandidateToJob.info.duplicateEmailHighlight')}
                <br />• {t('addCandidateToJob.info.phoneFormatNote')}
                <br />
                <br />
                <p>
                  <strong>{t('addCandidateToJob.info.warning.invalidEmailsIgnored')}</strong>
                  <br />
                  <strong>{t('addCandidateToJob.info.warning.invalidPhonesNoSms')}</strong>
                  <br />
                  <strong>{t('addCandidateToJob.info.warning.duplicatesIgnored')}</strong>
                </p>
                <Button variant="ghost" className="m-0" onClick={() => setShowInfoMore(false)}>
                  {t('addCandidateToJob.info.showLessButton')}
                </Button>
              </>
            )}
          </Callout.Text>
        </Callout.Root>

        <div className="flex items-center justify-between">
          <div className="border rounded flex items-center p-1 clickable">
            <Label.Root className="m-0 mr-2 clickable" htmlFor="csvUpload">
              {t('addCandidateToJob.csv.uploadLabel')}
            </Label.Root>
            <input
              key={fileInputKey}
              id="csvUpload"
              type="file"
              accept=".csv"
              className="clickable"
              onChange={handleFileUpload}
            />
          </div>
          <div className="flex items-end justify-end">
            {uploadedCandidates.length > 0 && (
              <Button variant="outline" color="amber" className="mr-2" onClick={clearAllCandidates}>
                {t('addCandidateToJob.buttons.clearAll')}
              </Button>
            )}
            <Button variant="outline" onClick={addEmptyCandidate}>
              {t('addCandidateToJob.buttons.addRow')}
            </Button>
          </div>
        </div>

        <div className="table-wrapper mt-4">
          <Table.Root size="1">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>{t('addCandidateToJob.table.firstName')}</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>{t('addCandidateToJob.table.lastName')}</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>{t('addCandidateToJob.table.phone')}</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>
                  {t('addCandidateToJob.table.email')} <span className="text-red-600">*</span>
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>{t('addCandidateToJob.table.status')}</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell></Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {uploadedCandidates.length === 0 ? (
                <Table.Row className="w-full h-full">
                  <Table.Cell colSpan={6} className="text-center text-gray-500 py-6">
                    {t('addCandidateToJob.emptyState')}
                  </Table.Cell>
                </Table.Row>
              ) : (
                uploadedCandidates.map((c, idx) => {
                  const isDuplicate = duplicateIndexSet.has(idx);
                  return (
                    <Table.Row key={idx} className={isDuplicate ? 'duplicate-row' : undefined} align="center">
                      <Table.Cell>
                        <TextField.Root
                          value={c.firstName}
                          onChange={(e) => {
                            handleCandidateChange(idx, 'firstName', e.target.value);
                          }}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <TextField.Root
                          value={c.lastName}
                          onChange={(e) => {
                            handleCandidateChange(idx, 'lastName', e.target.value);
                          }}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <PhoneInput
                          placeholder={t('addCandidateToJob.phonePlaceholder')}
                          value={c.phone ?? ''}
                          onChange={(value) => {
                            handleCandidateChange(idx, 'phone', value);
                          }}
                        />
                      </Table.Cell>
                      <Table.Cell className={!validateEmail(c.email) ? 'bg-red-600 text-white' : undefined}>
                        <TextField.Root
                          value={c.email}
                          onChange={(e) => {
                            const value = e.target.value.trim().toLowerCase();
                            handleCandidateChange(idx, 'email', value);
                          }}
                        />
                      </Table.Cell>
                      <Table.Cell className="status-cell align-middle" justify="center">
                        {inviteCandidatesToJobLoading ? (
                          <span title={t('addCandidateToJob.statusTitle.inviting')}>
                            <Loader className="text-blue-500 animate-spin" />
                          </span>
                        ) : (
                          <>
                            {isDuplicate && !c.status && (
                              <span title={t('addCandidateToJob.statusTitle.duplicateEmail')}>
                                <TriangleAlert className="text-yellow-500" />
                              </span>
                            )}
                            {c.status === 'success' && (
                              <span title={c.statusMessage ?? t('addCandidateToJob.statusTitle.invited')}>
                                <CircleCheck className="text-green-600" />
                              </span>
                            )}
                            {c.status === 'error' && (
                              <span title={c.statusMessage ?? t('addCandidateToJob.statusTitle.error')}>
                                <CircleX className="text-red-600" />
                              </span>
                            )}
                          </>
                        )}
                      </Table.Cell>
                      <Table.Cell justify="end">
                        <IconButton variant="ghost" color="red" className="m-0" onClick={() => removeCandidate(idx)}>
                          <CircleMinus />
                        </IconButton>
                      </Table.Cell>
                    </Table.Row>
                  );
                })
              )}
            </Table.Body>
          </Table.Root>
        </div>
        <div className="flex justify-end pt-4">
          {importDone ? (
            <Dialog.Close>
              <Button
                size="3"
                onClick={() => {
                  setUploadedCandidates([]);
                  setImportDone(false);
                }}
              >
                {t('addCandidateToJob.buttons.close')}
              </Button>
            </Dialog.Close>
          ) : (
            <Button
              size="3"
              disabled={uploadedCandidates.length === 0 || inviteCandidatesToJobLoading || isSubmitting}
              loading={inviteCandidatesToJobLoading || isSubmitting}
              onClick={handleSubmit}
            >
              {t('addCandidateToJob.buttons.inviteCandidates')}
            </Button>
          )}
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default AddCandidateToJob;
